import React from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';

interface Props {
   page: number; // 1-indexed
   pageCount: number;
   onChange: (page: number) => void;
   /** Total result count, optional — rendered as "Showing X–Y of Z". */
   totalCount?: number;
   pageSize?: number;
}

const Pagination: React.FC<Props> = ({
   page,
   pageCount,
   onChange,
   totalCount,
   pageSize,
}) => {
   if (pageCount <= 1 && totalCount === undefined) return null;
   const safePage = Math.max(1, Math.min(page, pageCount || 1));
   const fromIdx = pageSize ? (safePage - 1) * pageSize + 1 : undefined;
   const toIdx =
      pageSize && totalCount !== undefined
         ? Math.min(safePage * pageSize, totalCount)
         : undefined;

   return (
      <Wrap>
         {totalCount !== undefined && pageSize && (
            <RangeText>
               Showing {fromIdx}–{toIdx} of {totalCount}
            </RangeText>
         )}
         <PagerCtrls>
            <PagerBtn
               disabled={safePage <= 1}
               onClick={() => onChange(safePage - 1)}
            >
               ← Prev
            </PagerBtn>
            <PagerNum>
               {safePage} / {Math.max(1, pageCount)}
            </PagerNum>
            <PagerBtn
               disabled={safePage >= pageCount}
               onClick={() => onChange(safePage + 1)}
            >
               Next →
            </PagerBtn>
         </PagerCtrls>
      </Wrap>
   );
};

const Wrap = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   margin-top: 12px;
   padding: 0 4px;
   @media (max-width: 600px) {
      flex-direction: column;
      align-items: stretch;
   }
`;

const RangeText = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const PagerCtrls = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   margin-left: auto;
`;

const PagerBtn = styled.button`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   color: ${colors.GRAY1};
   padding: 5px 10px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
   cursor: pointer;
   &:hover:not(:disabled) {
      border-color: ${colors.PRIMARY};
      color: ${colors.PRIMARY};
   }
   &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
   }
`;

const PagerNum = styled.span`
   font-size: 11px;
   color: ${colors.GRAY3};
   font-weight: 700;
   min-width: 56px;
   text-align: center;
`;

export default Pagination;
